import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  PictureAsPdf as PdfIcon,
  Add as AddIcon
} from '@mui/icons-material';
import AddManufacturedProductDialog from '../dialogs/AddManufacturedProductDialog';
import EditManufacturedProductDialog from '../dialogs/EditManufacturedProductDialog';
import ManufacturingFinalizationButton from './ManufacturingFinalizationButton';

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' 
  ? 'https://barebiologics-api-bdgjaefwc4h7d9gu.eastus2-01.azurewebsites.net/api'
  : 'http://localhost:8080/api');

const formatDate = (date) => {
  if (!date) return '-';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
};

const ManufacturedTab = ({ donor, onRefresh }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [addProductDialogOpen, setAddProductDialogOpen] = useState(false);
  const [editProductDialogOpen, setEditProductDialogOpen] = useState(false);
  const [editingVial, setEditingVial] = useState(null);
  const fileInputRef = useRef(null);

  const processingRecords = donor?.processingRecords || [];
  const manufacturedVials = donor?.manufacturedVials || [];
  const isFinalized = donor?.manufacturingFinalized || false;

  const totalVials = manufacturedVials.reduce((sum, v) => sum + (v.quantity || 0), 0);

  const handleAddManufacturedProducts = async (manufacturingData) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/donors/${donor.id}/manufacture`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(manufacturingData)
      });

      const data = await response.json();

      if (!response.ok) {
        // Show the actual error message from the backend
        const errorMessage = data.message || data.error || 'Failed to add manufactured products';
        throw new Error(errorMessage);
      }

      setSuccess(`Successfully added ${manufacturingData.vials.length} manufactured product(s)`);
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('Error adding manufactured products:', err);
      setError(err.message || 'Failed to add manufactured products');
    }
  };

  const handleFinalizationComplete = async (updatedDonor) => {
    setSuccess('Manufacturing finalized successfully');
    if (onRefresh) await onRefresh();
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setError('Only PDF, DOC, DOCX, JPG, and PNG files are supported.');
      event.target.value = '';
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('authToken');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_BASE_URL}/donors/${donor.id}/processing-records/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to upload processing record');
      }

      setSuccess(`${file.name} uploaded successfully`);
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('Error uploading processing record:', err);
      setError(err.message || 'Failed to upload processing record');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleDeleteRecord = async (recordId) => {
    if (!window.confirm('Are you sure you want to delete this processing record?')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/donors/${donor.id}/processing-records/${recordId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete processing record');
      }

      setSuccess('Processing record deleted successfully');
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('Error deleting processing record:', err);
      setError(err.message || 'Failed to delete processing record');
    }
  };

  const handleEditVial = (vial) => {
    setEditingVial(vial);
    setEditProductDialogOpen(true);
  };

  const handleUpdateVial = async (updateData) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/donors/${donor.id}/manufactured-vials/${editingVial.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = data.message || data.error || 'Failed to update manufactured vial';
        throw new Error(errorMessage);
      }

      setSuccess('Manufactured vial updated successfully');
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('Error updating manufactured vial:', err);
      setError(err.message || 'Failed to update manufactured vial');
      throw err; // Re-throw to let dialog handle it
    }
  };

  const handleDeleteVial = async (vialId) => {
    if (!window.confirm('Are you sure you want to delete this manufactured vial? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/donors/${donor.id}/manufactured-vials/${vialId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Failed to delete manufactured vial');
      }

      setSuccess('Manufactured vial deleted successfully');
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('Error deleting manufactured vial:', err);
      setError(err.message || 'Failed to delete manufactured vial');
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Summary Card */}
      <Card sx={{ mb: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={4}>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>Total Vials Made</Typography>
              <Typography variant="h4" fontWeight="bold">{totalVials}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>Product Types</Typography>
              <Typography variant="h4" fontWeight="bold">{manufacturedVials.length}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>Lot Number</Typography>
              <Typography variant="h6" fontWeight="bold">{donor?.donorId || '-'}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddProductDialogOpen(true)}
          disabled={isFinalized}
        >
          Add Manufactured Product
        </Button>
        
        <ManufacturingFinalizationButton
          donor={donor}
          onFinalize={handleFinalizationComplete}
          disabled={manufacturedVials.length === 0}
        />
      </Box>
      {/* Manufactured Vials Table */}
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Manufactured Vials
      </Typography>

      {manufacturedVials.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          No vials have been manufactured yet. Use the Manufacturing workflow to add vials.
        </Alert>
      ) : (
        <TableContainer sx={{ mb: 4 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell><strong>Product Code</strong></TableCell>
                <TableCell><strong>Product Name</strong></TableCell>
                <TableCell align="center"><strong>Vials Made</strong></TableCell>
                <TableCell><strong>Size</strong></TableCell>
                <TableCell><strong>Expiration Date</strong></TableCell>
                <TableCell><strong>Manufacturing Date</strong></TableCell>
                <TableCell><strong>Lot Number</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                {!isFinalized && <TableCell align="center"><strong>Actions</strong></TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {manufacturedVials.map((vial) => (
                <TableRow key={vial.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace" fontWeight="bold">
                      {vial.productSku}
                    </Typography>
                  </TableCell>
                  <TableCell>{vial.productName}</TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={vial.quantity} 
                      color="primary" 
                      size="small"
                      sx={{ fontWeight: 'bold' }}
                    />
                  </TableCell>
                  <TableCell>{vial.vialSize || '-'}</TableCell>
                  <TableCell>{formatDate(vial.expirationDate)}</TableCell>
                  <TableCell>{formatDate(vial.manufacturingDate || vial.manufacturedAt)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {vial.lotNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={vial.status} 
                      size="small"
                      color={vial.status === 'RELEASED' ? 'success' : vial.status === 'QUARANTINE' ? 'warning' : 'default'}
                    />
                  </TableCell>
                  {!isFinalized && (
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => handleEditVial(vial)}
                          title="Edit vial"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleDeleteVial(vial.id)}
                          title="Delete vial"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Processing Records Section */}
      <Box sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Processing Records
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Upload up to 3 processing record documents
            </Typography>
          </Box>
          
          {processingRecords.length < 3 && (
            <>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={handleFileChange}
              />
              <Button
                variant="contained"
                startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                onClick={handleUploadClick}
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : 'Upload Record'}
              </Button>
            </>
          )}
        </Box>

        {processingRecords.length === 0 ? (
          <Alert severity="info">
            No processing records uploaded yet. Upload up to 3 documents.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {processingRecords.map((record, index) => (
              <Card key={record.id} variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <PdfIcon color="error" />
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {record.fileName || `Processing Record ${index + 1}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Uploaded {formatDate(record.uploadedAt)} by {record.uploadedByName || 'Unknown'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box>
                    {record.documentUrl && (
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => window.open(record.documentUrl, '_blank')}
                      >
                        <ViewIcon />
                      </IconButton>
                    )}
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => handleDeleteRecord(record.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Card>
            ))}
          </Box>
        )}

        {processingRecords.length >= 3 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Maximum of 3 processing records reached. Delete an existing record to upload a new one.
          </Alert>
        )}
      </Box>

      {/* Add Manufactured Product Dialog */}
      <AddManufacturedProductDialog
        open={addProductDialogOpen}
        onClose={() => setAddProductDialogOpen(false)}
        onAdd={handleAddManufacturedProducts}
        donor={donor}
      />

      {/* Edit Manufactured Product Dialog */}
      <EditManufacturedProductDialog
        open={editProductDialogOpen}
        onClose={() => {
          setEditProductDialogOpen(false);
          setEditingVial(null);
        }}
        onUpdate={handleUpdateVial}
        donor={donor}
        vial={editingVial}
      />
    </Box>
  );
};

export default ManufacturedTab;

